import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Alert from "@mui/material/Alert";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Divider from "@mui/material/Divider";

import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import ConfirmDialog from "../../common/ConfirmDialog";
import ConnectorWizard from "./ConnectorWizard";
import GiteaWebhookCard from "./GiteaWebhookCard";
import {
    listAppTokens,
    createAppToken,
    revokeAppToken,
} from "../../../redux/actions/AppTokenActions";
import { APP_TOKEN_CREATE_RESET } from "../../../redux/constants/AppTokenConstants";

const formatDate = (value) => {
    if (!value) return "Never";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString();
};

const copyToClipboard = async (text, successMsg = "Copied.") => {
    try {
        await navigator.clipboard.writeText(text);
        toast.success(successMsg);
    } catch (e) {
        toast.error("Couldn't copy to clipboard — select and copy manually.");
    }
};

const IntegrationsSection = ({ appId }) => {
    const dispatch = useDispatch();

    const appTokenList = useSelector((state) => state.appTokenList) || {};
    const { tokens = [], loading: listLoading } = appTokenList;

    const appTokenCreate = useSelector((state) => state.appTokenCreate) || {};
    const { loading: createLoading, createdToken } = appTokenCreate;

    const appTokenRevoke = useSelector((state) => state.appTokenRevoke) || {};
    const { loading: revokeLoading } = appTokenRevoke;

    const [createOpen, setCreateOpen] = useState(false);
    const [labelInput, setLabelInput] = useState("");
    const [revokeTarget, setRevokeTarget] = useState(null);

    useEffect(() => {
        if (appId) {
            dispatch(listAppTokens(appId));
        }
    }, [appId, dispatch]);

    const openCreate = () => {
        setLabelInput("");
        setCreateOpen(true);
    };

    const submitCreate = () => {
        const label = labelInput.trim();
        if (!label) {
            toast.error("Please enter a label to identify this token.");
            return;
        }
        dispatch(createAppToken(appId, label));
    };

    const closeRevealDialog = () => {
        dispatch({ type: APP_TOKEN_CREATE_RESET });
        setCreateOpen(false);
        setLabelInput("");
    };

    const askRevoke = (token) => setRevokeTarget(token);
    const confirmRevoke = () => {
        if (!revokeTarget) return;
        dispatch(revokeAppToken(appId, revokeTarget.id));
        setRevokeTarget(null);
    };

    const hasRevealDialog = Boolean(createdToken?.token);

    return (
        <Paper
            elevation={0}
            sx={{ p: { xs: 2, md: 3 }, border: "1px solid", borderColor: "divider", mb: 2 }}
        >
            <Typography variant="h4" component="h2" sx={{ mb: 2 }}>
                Integrations
            </Typography>

            <Stack spacing={3}>
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    alignItems={{ xs: "stretch", sm: "center" }}
                >
                    <TextField
                        label="App ID"
                        value={appId || ""}
                        InputProps={{
                            readOnly: true,
                            endAdornment: (
                                <IconButton
                                    edge="end"
                                    size="small"
                                    aria-label="copy app id"
                                    onClick={() => copyToClipboard(String(appId), "App ID copied.")}
                                >
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            ),
                        }}
                        sx={{ maxWidth: { sm: 280 } }}
                    />
                    <Box sx={{ flexGrow: 1 }} />
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={openCreate}
                        disabled={createLoading}
                    >
                        Create token
                    </Button>
                </Stack>

                <Divider />

                <Box>
                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                        API tokens
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.5 }}>
                        Tokens authenticate CI pipelines against this app's build-upload endpoint.
                        The plaintext token is shown only once, at creation.
                    </Typography>

                    {tokens.length === 0 && !listLoading ? (
                        <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic" }}>
                            No tokens yet. Create one to authenticate a CI pipeline.
                        </Typography>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Label</TableCell>
                                    <TableCell>Prefix</TableCell>
                                    <TableCell>Created</TableCell>
                                    <TableCell>Last used</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {tokens.map((t) => {
                                    const revoked = Boolean(t.revoked_at);
                                    return (
                                        <TableRow key={t.id} sx={{ opacity: revoked ? 0.55 : 1 }}>
                                            <TableCell>{t.label || "—"}</TableCell>
                                            <TableCell
                                                sx={{
                                                    fontFamily:
                                                        '"SF Mono", Menlo, Consolas, monospace',
                                                    fontSize: 13,
                                                }}
                                            >
                                                bqt_{t.prefix}…
                                            </TableCell>
                                            <TableCell>{formatDate(t.created_at)}</TableCell>
                                            <TableCell>{formatDate(t.last_used_at)}</TableCell>
                                            <TableCell>
                                                {revoked ? (
                                                    <Chip label="Revoked" size="small" color="default" />
                                                ) : (
                                                    <Chip label="Active" size="small" color="success" />
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                {!revoked && (
                                                    <IconButton
                                                        size="small"
                                                        aria-label={`revoke token ${t.label || t.prefix}`}
                                                        onClick={() => askRevoke(t)}
                                                        disabled={revokeLoading}
                                                    >
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )}
                </Box>

                <Divider />

                <GiteaWebhookCard appId={appId} />

                <Divider />

                <Accordion defaultExpanded elevation={0} disableGutters sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, "&:before": { display: "none" } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Set up your CI pipeline
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <ConnectorWizard appId={appId} />
                    </AccordionDetails>
                </Accordion>
            </Stack>

            {/* Create-token dialog (label input). Swaps to reveal dialog on success. */}
            <Dialog
                open={createOpen && !hasRevealDialog}
                onClose={createLoading ? undefined : () => setCreateOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 700 }}>Create API token</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2, color: "text.secondary" }}>
                        Give this token a label that identifies where it will be used
                        (for example, "Jenkins production").
                    </DialogContentText>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Label"
                        value={labelInput}
                        onChange={(e) => setLabelInput(e.target.value.slice(0, 64))}
                        helperText={`${labelInput.length}/64`}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setCreateOpen(false)} disabled={createLoading} color="inherit">
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={submitCreate}
                        disabled={createLoading || !labelInput.trim()}
                    >
                        {createLoading ? "Creating…" : "Create"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* One-shot reveal dialog. Only renders while Redux holds the plaintext. */}
            <Dialog open={hasRevealDialog} onClose={closeRevealDialog} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>Token created</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        This token is shown only once. Copy it now and store it in your CI
                        secret vault before closing this dialog.
                    </Alert>
                    <TextField
                        fullWidth
                        value={createdToken?.token || ""}
                        InputProps={{
                            readOnly: true,
                            sx: {
                                fontFamily:
                                    '"SF Mono", Menlo, Consolas, monospace',
                                fontSize: 13,
                            },
                            endAdornment: (
                                <IconButton
                                    edge="end"
                                    size="small"
                                    aria-label="copy token"
                                    onClick={() =>
                                        copyToClipboard(createdToken?.token || "", "Token copied.")
                                    }
                                >
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            ),
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button variant="contained" onClick={closeRevealDialog}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={Boolean(revokeTarget)}
                title="Revoke API token?"
                body={
                    revokeTarget
                        ? `Revoke "${revokeTarget.label || revokeTarget.prefix}" (bqt_${revokeTarget.prefix}…)? Any CI pipeline using this token will stop working immediately.`
                        : ""
                }
                confirmLabel="Revoke"
                confirmColor="error"
                onCancel={() => setRevokeTarget(null)}
                onConfirm={confirmRevoke}
                loading={revokeLoading}
            />
        </Paper>
    );
};

export default IntegrationsSection;
